import { useTranslation } from 'react-i18next';
import {
  ApiToken,
  ApiTokenStatusEnum,
  useApiTokenCreateMutation,
  useApiTokenDestroyMutation,
  useApiTokenUpdateMutation,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import useApiTokensQueryHook from 'utils/fetch/hooks/useApiTokensQueryHook';
import useApiTokenQueryHook from 'utils/fetch/hooks/useApiTokenQueryHook';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { Modals } from '@lib/enums/modals';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';

interface Props {
  useBulkSelectResult: UseBulkSelectResult<ApiToken>;
  useModalManagementResult: UseModalManagementResult;
  setCreatedApiToken: (data: ApiToken | null) => void;
}

export interface UseActionsResult {
  apiTokenCreateMutationResult: ReturnType<typeof useApiTokenCreateMutation>[1];
  apiTokenDestroyLoading: boolean;
  apiTokenQuery: ReturnType<typeof useApiTokenQueryHook>;
  apiTokenUpdateMutationResult: ReturnType<typeof useApiTokenUpdateMutation>[1];
  apiTokensQuery: ReturnType<typeof useApiTokensQueryHook>;
  isCreateApiTokenAvailable: boolean;
  onCloseAddApiTokenModal: () => void;
  onCloseDestroyApiTokenModal: () => void;
  onCloseHowToUseApiModal: () => void;
  onConfirmedDelete: () => void;
  onDestroyApiToken: (token?: ApiToken) => void;
  onOpenEditApiTokenModal: (token?: ApiToken) => void;
  onStatusChangeHandler: (id: string, status: ApiTokenStatusEnum) => void;
  onSubmitApiTokenForm: (values: Values) => void;
}

export default function useActions(props: Props): UseActionsResult {
  const { useModalManagementResult, useBulkSelectResult, setCreatedApiToken } =
    props;
  const { openModal, closeModal } = useModalManagementResult;

  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  // Queries
  const apiTokensQuery = useApiTokensQueryHook({
    paginationParams,
    searchQuery,
  });
  const apiTokenQuery = useApiTokenQueryHook({
    id: useBulkSelectResult.selectedSingleTableItem?.id,
    skip: !useBulkSelectResult.selectedSingleTableItem?.id,
  });

  // Mutations
  const [apiTokenCreateMutation, apiTokenCreateMutationResult] =
    useApiTokenCreateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [apiTokenUpdateMutation, apiTokenUpdateMutationResult] =
    useApiTokenUpdateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [apiTokenDestroyMutation, apiTokenDestroyMutationResult] =
    useApiTokenDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  // Actions
  const onSubmitApiTokenForm = (values: Values) => {
    if (useBulkSelectResult.selectedSingleTableItem?.id) {
      apiTokenUpdateMutation({
        variables: {
          attributes: values,
          id: useBulkSelectResult.selectedSingleTableItem.id,
        },
        onCompleted: () => {
          closeModal(Modals.ApiTokenFormModal);
          apiTokensQuery.refetch();
          toast({ content: t('update-api-token-success') });
        },
      });
    } else {
      apiTokenCreateMutation({
        variables: { attributes: values },
        onCompleted: (response) => {
          setCreatedApiToken(response.data as ApiToken);
          apiTokensQuery.refetch();
        },
      });
    }
  };

  const onOpenEditApiTokenModal = (token?: ApiToken) => {
    if (token) {
      useBulkSelectResult.handleSelectSingleTableItem(token);
      apiTokenQuery.refetch();
      openModal(Modals.ApiTokenFormModal);
    }
  };

  const onDestroyApiToken = (token?: ApiToken) => {
    if (token?.id) {
      useBulkSelectResult.handleSelectSingleTableItem(token);
      openModal(Modals.ApiTokenDestroyModal);
    }
  };

  const onCloseAddApiTokenModal = () => {
    closeModal(Modals.ApiTokenFormModal);
    setCreatedApiToken(null);
    useBulkSelectResult.handleUnselectAllTableItems();
    apiTokenCreateMutationResult.reset();
  };

  const onCloseDestroyApiTokenModal = () => {
    closeModal(Modals.ApiTokenDestroyModal);
    setCreatedApiToken(null);
    apiTokenCreateMutationResult.reset();
  };

  const onCloseHowToUseApiModal = () => closeModal(Modals.HowToUseApi);

  const onConfirmedDelete = () => {
    const id = useBulkSelectResult.selectedSingleTableItem?.id;
    if (id) {
      apiTokenDestroyMutation({
        variables: { id },
        onCompleted: () => {
          closeModal(Modals.ApiTokenDestroyModal);
          toast({ content: t('destroy-api-token-success') });
          apiTokensQuery.refetch();
          useBulkSelectResult.handleUnselectAllTableItems();
        },
      });
    }
  };

  const onStatusChangeHandler = (id: string, status: ApiTokenStatusEnum) => {
    if (id && status) {
      apiTokenUpdateMutation({
        variables: {
          attributes: { status },
          id,
        },
        onCompleted: () => {
          apiTokensQuery.refetch();
          toast({ content: t('update-api-token-success') });
        },
      });
    }
  };

  const isCreateApiTokenAvailable =
    !apiTokensQuery.loading && apiTokensQuery.collection?.length === 0;

  return {
    apiTokenCreateMutationResult,
    apiTokenDestroyLoading: apiTokenDestroyMutationResult.loading,
    apiTokenQuery,
    apiTokenUpdateMutationResult,
    apiTokensQuery,
    isCreateApiTokenAvailable,
    onCloseAddApiTokenModal,
    onCloseDestroyApiTokenModal,
    onCloseHowToUseApiModal,
    onConfirmedDelete,
    onDestroyApiToken,
    onOpenEditApiTokenModal,
    onStatusChangeHandler,
    onSubmitApiTokenForm,
  };
}
